import { useCallback, useMemo } from 'react';
import { useSwipeable, type SwipeEventData } from 'react-swipeable';

/**
 * How far a finger has to travel before it counts as changing tab. Well above
 * the noise of a tap and of the small sideways drift in a vertical scroll.
 */
const SWIPE_DISTANCE = 70;

/**
 * Mark any element that scrolls sideways on its own — a wide chart, a table, a
 * carousel — and swipes that start inside it are left alone. Without this the
 * same finger movement both scrolls the thing and navigates away from it.
 */
export const NO_SWIPE_ATTRIBUTE = 'data-no-swipe-nav';

interface SwipeTabsOptions {
  /** The tabs, in the order they are shown. */
  tabIds: string[];
  /** The one currently open. */
  activeId: string | null;
  onSelect: (id: string) => void;
  /** Off while something else owns the gesture — a drawer, a drag. */
  enabled?: boolean;
}

/**
 * Left/right swipes step through a set of tabs.
 *
 * Vertical scrolling is untouched: nothing here calls `preventDefault`, and
 * react-swipeable only reports a horizontal swipe when the sideways travel beat
 * the vertical one — so a normal scroll down a long list never registers.
 *
 * Spread the returned handlers over the tab's **content**, not over the tab
 * strip: the strip scrolls sideways itself.
 */
export function useSwipeTabs({ tabIds, activeId, onSelect, enabled = true }: SwipeTabsOptions) {
  const step = useCallback((delta: number, event: SwipeEventData) => {
    if (!enabled || tabIds.length < 2) return;

    // A swipe that began on something with its own sideways scroll belongs to
    // that thing.
    const target = event.event.target;
    if (target instanceof Element && target.closest(`[${NO_SWIPE_ATTRIBUTE}]`)) return;

    const current = activeId ? tabIds.indexOf(activeId) : -1;
    if (current === -1) return;

    const next = current + delta;
    // The ends are ends — no wrapping. Coming back round to the first tab from
    // the last one reads as a bug, not as a feature.
    if (next < 0 || next >= tabIds.length) return;

    onSelect(tabIds[next]);
  }, [enabled, tabIds, activeId, onSelect]);

  const handlers = useSwipeable(useMemo(() => ({
    // Swiping left moves you forward through the strip, the way the content
    // itself would slide.
    onSwipedLeft: (event) => step(1, event),
    onSwipedRight: (event) => step(-1, event),
    delta: SWIPE_DISTANCE,
    preventScrollOnSwipe: false,
    trackMouse: false,
  }), [step]));

  return handlers;
}
